import { DefaultLayout } from "@westpierstudio/luma/layout";
import { AssetSpec, SpriteAssetSpec, TextAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { Container, Sprite, Text } from "pixi.js";
import { Tween } from "@westpierstudio/luma/createjs";
import { Signal } from "type-signals";
import { getSoundPlayer } from "sound/SoundLocator";
import { Resettable } from "@westpierstudio/luma/utils/reset";
import { BoundedContainer, Bounds, implementBoundedContainer } from "utils/BoundedContainer";
import { OrientationSpec } from "servertypes/SharedTypes";

interface RetriggerWinPanelSpriteSpec extends SpriteAssetSpec {
    limitValue: number
    position: OrientationSpec<AssetSpec>
    textBounds: Bounds
    retriggerWinPanel: SpriteAssetSpec
    retriggerWinTopText1: TextAssetSpec
    retriggerWinTopText2: TextAssetSpec
    retriggerWinNumber: TextAssetSpec
    retriggerWinBottomText1: TextAssetSpec
    retriggerWinBottomText2: TextAssetSpec
    retriggerTotalDisplayTime: number
    retriggerLimitTopText: TextAssetSpec
    retriggerLimitBottomText: TextAssetSpec
}

class RetriggerWinPanelSignals {
    complete = new Signal();
    skip = new Signal();
}

export class RetriggerWinPanel extends View implements Resettable {
    private spec: RetriggerWinPanelSpriteSpec;
    private sprite: Sprite;

    private additionalNumberText: Text;
    private additionalNumberBounds: BoundedContainer;

    private containerRetrigger: Container;
    private containerRetriggerLimit: Container;

    signals = new RetriggerWinPanelSignals();

    constructor(protected override readonly services: GameServices, layout: DefaultLayout) {
        super(services);
        const { factory, resources } = this.services;

        this.spec = resources.getSpec(`gamewinpanels`);
        factory.applySpec(this, this.spec.position[layout.orientation]);

        this.sprite = factory.createSprite(this.spec.retriggerWinPanel, this);
        this.sprite.x -= this.sprite.width / 2;
        this.sprite.y -= this.sprite.height / 2;

        this.containerRetrigger = factory.createContainer({}, this);
        this.containerRetriggerLimit = factory.createContainer({ visible: false }, this);

        implementBoundedContainer(factory.createText(resources.getMessage(`retriggerWinTopText1`), this.spec.retriggerWinTopText1, this.containerRetrigger), this.spec.textBounds);
        implementBoundedContainer(factory.createText(resources.getMessage(`retriggerWinTopText2`), this.spec.retriggerWinTopText2, this.containerRetrigger), this.spec.textBounds);
        this.additionalNumberBounds = implementBoundedContainer(this.additionalNumberText = factory.createText(`000`, this.spec.retriggerWinNumber, this), this.spec.textBounds);
        implementBoundedContainer(factory.createText(resources.getMessage(`retriggerWinBottomText1`), this.spec.retriggerWinBottomText1, this.containerRetrigger), this.spec.textBounds);
        implementBoundedContainer(factory.createText(resources.getMessage(`retriggerWinBottomText2`), this.spec.retriggerWinBottomText2, this.containerRetrigger), this.spec.textBounds);

        implementBoundedContainer(factory.createText(resources.getMessage(`free-spins-limit-1`), this.spec.retriggerLimitTopText, this.containerRetriggerLimit), this.spec.textBounds);
        implementBoundedContainer(factory.createText(resources.getMessage(`free-spins-limit-2`), this.spec.retriggerLimitBottomText, this.containerRetriggerLimit), this.spec.textBounds);

        this.signals.skip.add(() => this.slam());

        this.hide();

        layout.onReorient.add(() => this.reorient());
    }

    private reorient() {
        const { factory, layout } = this.services;
        factory.applySpec(this, this.spec.position[layout.orientation]);
    }

    show(gameNumber: number = 0) {
        const freeSpinLimit = gameNumber === 0;
        this.containerRetrigger.visible = !freeSpinLimit;
        this.containerRetriggerLimit.visible = freeSpinLimit;
        if (freeSpinLimit) gameNumber = this.spec.limitValue;
        else getSoundPlayer().retrigger(gameNumber);

        this.visible = true;
        this.additionalNumberText.text = gameNumber.toString();
        this.additionalNumberBounds.update();
        Tween.get(this)
            .wait(this.spec.retriggerTotalDisplayTime)
            .call(() => {
                this.hide();
                this.signals.complete.dispatch();
            });
    }

    hide() {
        this.visible = false;
    }

    slam() {
        if (this.visible) {
            Tween.removeTweens(this);
            this.signals.complete.dispatch();
            this.hide();
        }
    }

    override reset(): void {
        Tween.removeTweens(this);
        this.hide();
    }
}